import React, { useState } from "react";
import { toast } from "sonner";
import { MapPinLine } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { STAGES } from "./_helpers";

export function BulkMoveButton({ onMoved }) {
  const [open, setOpen] = useState(false);
  const [fromStage, setFromStage] = useState("Concurrent 3");
  const [toStage, setToStage] = useState("Concurrent 1");
  const [date, setDate] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (fromStage === toStage) return toast.error("Pick two different stages");
    setBusy(true);
    try {
      const { data } = await api.post("/sessions/bulk-move", {
        from_stage: fromStage, to_stage: toStage,
        date: date || null, notify,
      });
      const sent = data?.push?.sent;
      const summary = notify
        ? `Moved ${data.moved} session(s) · push sent to ${sent ?? 0}`
        : `Moved ${data.moved} session(s)`;

      if (data.moved > 0 && data.undo_available) {
        toast.success(summary, {
          duration: 10000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                const { data: u } = await api.post(`/admin/undo-last-bulk-move?notify=${notify}`);
                toast.success(`Reverted ${u.reverted} session(s)`);
                onMoved?.();
              } catch (err) { toast.error(formatApiError(err)); }
            },
          },
        });
      } else {
        toast.success(summary);
      }
      setOpen(false);
      onMoved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="rounded-sm border-[#E5E9F0]" data-testid="admin-bulk-move-btn">
        <MapPinLine size={16} className="mr-1" /> Bulk move
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E5E9F0] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Bulk move rooms</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#5a5a5a] -mt-2">Move every session in one stage to another - optionally only for a single day.</p>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs text-[#7A828C]">From stage</Label>
              <Select value={fromStage} onValueChange={setFromStage}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="bulk-from-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#7A828C]">To stage</Label>
              <Select value={toStage} onValueChange={setToStage}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="bulk-to-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#7A828C]">Day (optional)</Label>
              <Select value={date || "all"} onValueChange={(v) => setDate(v === "all" ? "" : v)}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="bulk-date"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All days</SelectItem>
                  <SelectItem value="2026-06-22">Mon 22 Jun</SelectItem>
                  <SelectItem value="2026-06-23">Tue 23 Jun</SelectItem>
                  <SelectItem value="2026-06-24">Wed 24 Jun</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="bulk-notify" />
              <span>Push-notify all delegates</span>
            </label>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="bulk-submit-btn">
              {busy ? "Moving…" : "Move sessions"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function MoveRoomButton({ session, onMoved }) {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState(session.stage);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (stage === session.stage) { setOpen(false); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/sessions/${session.id}/move-room`, { stage, notify });
      const sent = data?.push?.sent;
      toast.success(notify ? `Moved to ${stage} · push sent to ${sent ?? 0}` : `Moved to ${stage}`);
      setOpen(false);
      onMoved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => { setStage(session.stage); setNotify(true); setOpen(true); }}
        className="w-10 h-10 grid place-items-center rounded-sm hover:bg-[#F0F2F7] text-[#000000]"
        title="Move to another room"
        data-testid={`move-session-${session.id}`}>
        <MapPinLine size={16} />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E5E9F0] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Move room</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-[#5a5a5a] truncate"><span className="font-medium text-[#1c1c24]">{session.title}</span></div>
            <div className="text-xs font-mono-time text-[#7A828C]">Currently in {session.stage}</div>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger className="rounded-sm" data-testid="move-stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="move-notify-checkbox" />
              <span>Push-notify all delegates</span>
            </label>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="move-submit-btn">
              {busy ? "Moving…" : "Move room"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
